// Helper function to parse boolean from string
const getBoolean = (
  value: string | undefined,
  defaultValue: boolean
): boolean => {
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === "true";
};

// Helper function to parse number from string
const getNumber = (value: string | undefined, defaultValue: number): number => {
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

export type BackendType = "go" | "worker";

// Define the shape of our configuration
interface AppConfig {
  /** Which backend to connect to: "go" (default) or "worker" (Cloudflare) */
  backend: BackendType;
  apiHost: string;
  wsHost: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  stripeEndpoint: string;
  minimumLobbySize: number;
  canReactToSelf: boolean;
  isPremiumEnabled: boolean;
  areAccountsEnabled: boolean;
  isDevelopment: boolean;
  isI18nDebug: boolean;

  version: string;
  fastTransition: boolean;
  longPaste: boolean;
  dontTwitch: boolean;
  doSonic: boolean;
  isWdyrEnabled: boolean;
}

// Determine backend type
const backend: BackendType =
  (import.meta.env.VITE_APP_BACKEND as BackendType) === "worker" ? "worker" : "go";

// Resolve hosts: when backend=worker, prefer VITE_APP_WORKER_*_HOST, falling
// back to the standard VITE_APP_*_HOST vars (so a single set of vars works too).
const apiHost =
  backend === "worker"
    ? import.meta.env.VITE_APP_WORKER_API_HOST ||
      import.meta.env.VITE_APP_API_HOST ||
      "http://localhost:8787/"
    : import.meta.env.VITE_APP_API_HOST || "http://localhost:8080/";

const wsHost =
  backend === "worker"
    ? import.meta.env.VITE_APP_WORKER_WS_HOST ||
      import.meta.env.VITE_APP_WS_HOST ||
      "ws://localhost:8787/ws"
    : import.meta.env.VITE_APP_WS_HOST || "ws://localhost:8080/ws";

// Create the config object by reading from import.meta.env and providing defaults
export const config: AppConfig = {
  backend,

  // API and WebSocket hosts
  apiHost,
  wsHost,

  // Auth0 configuration
  auth0Domain: import.meta.env.VITE_APP_AUTH0_DOMAIN || "",
  auth0ClientId: import.meta.env.VITE_APP_AUTH0_CLIENTID || "",
  auth0Audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE || "",

  // Stripe
  stripeEndpoint: import.meta.env.VITE_APP_STRIPE_ENDPOINT || "",

  // Game settings & feature flags
  minimumLobbySize: getNumber(import.meta.env.VITE_APP_MIN_LOBBY_SIZE, 1),
  canReactToSelf: getBoolean(import.meta.env.VITE_APP_SELF_REACT, false),
  isPremiumEnabled: getBoolean(import.meta.env.VITE_APP_PREMIUM, true),
  areAccountsEnabled: getBoolean(import.meta.env.VITE_APP_ACCOUNTS, true),

  // Development & Debugging flags
  isDevelopment: getBoolean(import.meta.env.VITE_APP_DEVELOPMENT, false),
  isI18nDebug: getBoolean(import.meta.env.VITE_APP_I18N_DEBUG, false),

  version: import.meta.env.VITE_APP_VERSION || "unknown",
  fastTransition: getBoolean(import.meta.env.VITE_APP_FAST_TRANSITION, false),
  longPaste: getBoolean(import.meta.env.VITE_APP_LONG_PASTE, false),
  dontTwitch: getBoolean(import.meta.env.VITE_APP_DONT_TWITCH, false),
  doSonic: getBoolean(import.meta.env.VITE_APP_DO_SONIC, false),
  isWdyrEnabled: getBoolean(import.meta.env.VITE_WDYR_ENABLED, false),
};
